import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../atoms/icon/icon.component';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { Router, RouterModule } from '@angular/router';

import { ContactItem } from '../../../shared/interfaces/contact-item.interface';
import { ModalComponent } from '../modal/modal.component';
import {
  CaasComponent,
  CaasEmailItems,
  CaasFormField,
  CaasLinksItems,
  CaasPhoneItems,
  CaasSimpleField,
} from '../../../shared/interfaces/caas/caas.interface';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { LinkComponent } from '../../atoms/link/link.component';
import { HrefType } from '../../../shared/enums/href-type.enums';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-contact-module',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    RouterModule,
    ModalComponent,
    LinkComponent,
    CapitalizePipe,
  ],
  templateUrl: './contact-module.component.html',
  styleUrls: ['./contact-module.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class ContactModuleComponent implements OnChanges, AfterViewInit {
  @Input() caasData?: CaasComponent;
  @Input() componentId?: string;
  @Input() headline: string = 'Default Headline';
  @Input() groups?: ContactItem[];
  @Input() columns: 'col-12' | 'col-6' = 'col-12';
  @Input() backgroundColor: Colors = Colors.WHITE;

  public modalContactItem: ContactItem['items'][number] = {
    linkText: '',
    href: '',
    icon: '',
    type: '',
    list: [],
  };

  showModal = false;
  BadgeType = BadgeType;
  Sizes = Sizes;
  isBgWhite = false;
  HrefType = HrefType;

  constructor(
    private caasRoutesService: CaasRoutesService,
    private elRef: ElementRef,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.setCaasContent();
    }
  }

  ngAfterViewInit(): void {
    this.checkParentClass();
  }

  checkParentClass() {
    const parentSection = this.elRef.nativeElement.closest('section');
    if (parentSection && parentSection.classList.contains('bg-grey')) {
      this.isBgWhite = true;
    } else {
      this.isBgWhite = false;
    }
  }

  setCaasContent() {
    if (!this.caasData?.formData.st_items?.value) {
      console.error('Invalid Caas Data');
      return;
    }

    this.headline = this.caasData.formData.st_header?.value ?? 'Default Header';
    this.componentId = this.caasData.identifier;
    this.getCaasColumns();
    this.groups = this.getCaasGroups();
  }

  getCaasColumns() {
    switch (this.caasData?.formData.st_columns?.value.identifier) {
      case '1':
        this.columns = 'col-12';
        break;
      case '2':
        this.columns = 'col-6';
        break;
      default:
        this.columns = 'col-6';
        break;
    }
  }

  getCaasGroups(): ContactItem[] {
    return (
      this.caasData?.formData.st_items?.value.map((contactItem) => {
        const items = this.getGroupsItems(
          contactItem.formData.st_email_items ?? ({} as CaasEmailItems),
          contactItem.formData.st_phone_items ?? ({} as CaasPhoneItems),
          contactItem.formData.st_links_items ?? ({} as CaasLinksItems),
          contactItem.formData.st_links_label ?? ({} as CaasSimpleField),
          contactItem.formData.st_links_title ?? ({} as CaasSimpleField)
        );

        return {
          label: this.getGroupsLabel(
            contactItem.formData.st_header ?? {
              fsType: '',
              name: '',
              value: '',
            },
            contactItem.formData.st_subheader ?? {
              fsType: '',
              name: '',
              value: '',
            }
          ),
          items: items || [],
        };
      }) ?? []
    );
  }

  getGroupsLabel(
    headline: CaasSimpleField,
    openingHours: CaasSimpleField
  ): string {
    if (openingHours.value) {
      return `<span><strong>${headline.value}</strong><br>${openingHours.value}</span>`;
    }
    return `<span><strong>${headline.value}</strong></span>`;
  }

  getGroupsItems(
    email: CaasEmailItems,
    phone: CaasPhoneItems,
    link: CaasLinksItems,
    linkLabel: CaasSimpleField,
    linkTitle: CaasSimpleField
  ): ContactItem['items'] {
    let items: ContactItem['items'] = [];
    items = this.setEmailItems(email, items);
    items = this.setPhoneItems(phone, items);
    items = this.setLinkItems(linkLabel, linkTitle, link, items);
    return items;
  }

  setEmailItems(
    email: CaasEmailItems,
    items: ContactItem['items']
  ): ContactItem['items'] {
    if (email.value.length > 0) {
      if (email.value.length === 1) {
        items = [
          ...items,
          {
            linkText: email.value[0].formData.lt_link_email.value!,
            href: `mailto:${email.value[0].formData.lt_link_email.value!}`,
            title: email?.value[0]?.formData?.lt_link_title?.value ?? '',
            icon: 'envelope',
            type: 'external',
          },
        ];
      } else {
        // TODO: define emailItem interface
        items = [
          ...items,
          {
            linkText: 'Email senden',
            modalTitle: 'E-Mail auswählen:',
            href: '',
            title: 'Email senden',
            list: email.value.map((emailItem) => {
              return {
                linkText: emailItem.formData.lt_link_email.value!,
                linkLabel: emailItem?.formData?.lt_link_label?.value ?? '',
                href: `mailto:${emailItem.formData.lt_link_email.value!}`,
                type: 'external',
                title: emailItem?.formData?.lt_link_title?.value ?? '',
              };
            }),
            icon: 'envelope',
            type: 'external',
          },
        ];
      }
    }
    return items;
  }

  setPhoneItems(
    phone: CaasPhoneItems,
    items: ContactItem['items']
  ): ContactItem['items'] {
    if (phone.value.length > 0) {
      if (phone.value.length === 1) {
        items = [
          ...items,
          {
            linkText: phone.value[0].formData.lt_link_phone.value!,
            href: `tel:${phone.value[0].formData.lt_link_phone.value!}`,
            title: phone?.value[0]?.formData?.lt_link_title?.value ?? '',
            icon: 'phone',
            type: 'external',
          },
        ];
      } else {
        // TODO: define phoneItem interface
        items = [
          ...items,
          {
            linkText: 'Anrufen',
            modalTitle: 'Telefonnummer auswählen',
            href: '',
            title: 'Anrufen',
            list: phone.value.map((phoneItem) => {
              return {
                linkText: phoneItem.formData.lt_link_phone.value!,
                linkLabel:
                  phoneItem?.formData?.lt_link_label?.value ?? undefined,
                href: `tel:${phoneItem.formData.lt_link_phone.value!}`,
                type: 'external',
                title: phoneItem?.formData?.lt_link_title?.value ?? '',
              };
            }),
            icon: 'phone',
            type: 'external',
          },
        ];
      }
    }
    return items;
  }

  setLinkItems(
    linkLabel: CaasSimpleField,
    linkTitle: CaasSimpleField,
    link: CaasLinksItems,
    items: ContactItem['items']
  ): ContactItem['items'] {
    if (link.value.length > 0) {
      if (link.value.length === 1) {
        const linkItem = link.value[0];
        const href = this.getCaasLinkHref(linkItem);
        const fragment = linkItem.formData.st_anchor_name?.value ?? '';
        return [
          ...items,
          {
            linkText: linkItem.formData.lt_link_label.value!,
            href,
            fragment,
            title: linkItem.formData.lt_link_title?.value ?? '',
            icon: 'globe',
            target: this.getCaasLinkTarget(linkItem.formData.lt_link_type),
            type: linkItem.formData.lt_link_type.value.identifier,
          },
        ];
      } else {
        return [
          ...items,
          {
            linkText: linkLabel?.value ?? '',
            modalTitle: linkLabel?.value ?? '',
            href: '',
            title: linkTitle?.value ?? '',
            icon: 'globe',
            list: link.value.map((linkItem) => {
              const href = this.getCaasLinkHref(linkItem);
              const fragment = linkItem.formData.st_anchor_name?.value ?? '';
              return {
                linkLabel: linkItem.formData.lt_link_label.value!,
                href,
                fragment,
                title: linkItem.formData.lt_link_title?.value ?? '',
                icon: 'globe',
                target: this.getCaasLinkTarget(linkItem.formData.lt_link_type),
                type: linkItem.formData.lt_link_type.value.identifier,
              };
            }),
          },
        ];
      }
    }
    return items;
  }
  getCaasLinkTarget(linkType: CaasFormField): string {
    switch (linkType.value.identifier) {
      case 'external':
        return 'new';
      default:
        return 'same';
    }
  }

  getCaasLinkHref(link: CaasLinksItems['value'][number]): string {
    const baseHref =
      link.formData.lt_link_type.value.identifier === 'external'
        ? link.formData.lt_link_external.value!
        : '/' +
          this.caasRoutesService.getSeoRouteByIdentifier(
            link.formData.lt_link_internal?.value?.identifier ?? '',
            this.router.config
          )?.path;

    return baseHref;
  }

  openModal(event: MouseEvent, item: ContactItem['items'][number]) {
    event.preventDefault();
    this.modalContactItem = item;
    this.showModal = true;
  }
}
